import React, { useState, useEffect } from 'react';
import { Form, Input, Button, DatePicker, Select, Upload, message, Typography, Row, Col, Space } from 'antd';
import { UserOutlined, MailOutlined, MobileOutlined, LockOutlined, UploadOutlined } from '@ant-design/icons';
import ImgCrop from 'antd-img-crop';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { resizeImage } from '../../utils/tools';

const { Title } = Typography;
const { Option } = Select;

const RegisterPage = () => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [avatarUrl, setAvatarUrl] = useState('');
    const [qrUrl, setQrUrl] = useState('');
    const [username, setUsername] = useState('');
    const [mathChallenge, setMathChallenge] = useState({ q: '', a: 0 });

    useEffect(() => {
        generateMathChallenge();
    }, []);

    const generateMathChallenge = () => {
        const a = Math.floor(Math.random() * 10);
        const b = Math.floor(Math.random() * 10);
        setMathChallenge({ q: `${a} + ${b} = ?`, a: a + b });
    };

    const onFinish = async (values) => {
        if (parseInt(values.verification) !== mathChallenge.a) {
            message.error('验证码错误，请重试');
            generateMathChallenge();
            form.setFieldsValue({ verification: '' });
            return;
        }

        setLoading(true);
        try {
            const payload = {
                username: values.username,
                email: values.email,
                mobile: values.mobile,
                password: values.password,
                gender: values.gender,
                birthDate: values.birthDate ? values.birthDate.format('YYYY-MM-DD') : null,
                avatarUrl: avatarUrl,
                qrUrl: qrUrl,
            };

            const res = await api.post('/users', payload);
            if (res && res.code === 200) {
                message.success('注册成功，请登录');
                navigate('/');
            } else {
                message.error(res.message || '注册失败');
                generateMathChallenge();
            }
        } catch (error) {
            console.error('Registration error:', error);
            message.error('注册请求失败，请稍后重试');
            generateMathChallenge();
        } finally {
            setLoading(false);
        }
    };

    const handleUsernameChange = (e) => {
        setUsername(e.target.value);
    };

    const customUpload = async ({ file, onSuccess, onError, title, fieldName }) => {
        try {
            // Resize image to max 200x200
            const resizedFile = await resizeImage(file, 200, 200);
            
            const formData = new FormData();
            formData.append('files', resizedFile);

            const res = await api.post('/uploads/public', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                params: {
                    title: title,
                    uploadMark: 'userProfile'
                }
            });

            if (res && res.code === 200 && res.data && res.data.length > 0) {
                const url = res.data[0].url;
                onSuccess(url);
                if (fieldName === 'avatarUrl') {
                    setAvatarUrl(url);
                } else if (fieldName === 'qrUrl') {
                    setQrUrl(url);
                }
                message.success('上传成功');
            } else {
                onError(new Error('Upload failed'));
                message.error('上传失败');
            }
        } catch (error) {
            console.error('Upload error:', error);
            onError(error);
            message.error('上传出错');
        }
    };

    const uploadProps = (fieldName, titleSuffix) => ({
        customRequest: (options) => customUpload({ 
            ...options, 
            title: `avatar-${username}${titleSuffix ? '-' + titleSuffix : ''}`, 
            fieldName 
        }),
        showUploadList: false,
        beforeUpload: (file) => {
            const isLt500K = file.size / 1024 < 500;
            if (!isLt500K) {
                message.error('图片大小不能超过 500KB!');
            }
            return isLt500K;
        },
    });

    return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh', padding: '20px' }}>
            <div style={{ width: '100%', maxWidth: '600px', background: '#fff', padding: '40px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                    <Title level={2}>用户注册</Title>
                </div>
                
                <Form
                    form={form}
                    name="register"
                    onFinish={onFinish}
                    layout="vertical"
                    size="large"
                    scrollToFirstError
                >
                    <Form.Item
                        name="username"
                        label="用户名"
                        tooltip="英文字母开头区分大小写，剩余可以字母、数字、下划线，长度不超过20"
                        rules={[
                            { required: true, message: '请输入用户名!', whitespace: true },
                            { pattern: /^[a-zA-Z][a-zA-Z0-9_]*$/, message: '必须以英文字母开头，只能包含字母、数字和下划线' },
                            { max: 20, message: '长度不超过20个字符' }
                        ]}
                    >
                        <Input prefix={<UserOutlined />} onChange={handleUsernameChange} placeholder="请输入用户名" />
                    </Form.Item>

                    <Form.Item
                        name="email"
                        label="电子邮箱"
                        tooltip="应为常用邮件，方便系统联络该用户"
                        rules={[
                            { type: 'email', message: '请输入有效的邮箱地址!' },
                            { required: true, message: '请输入邮箱!' },
                        ]}
                    >
                        <Input prefix={<MailOutlined />} placeholder="请输入常用邮箱" />
                    </Form.Item>

                    <Form.Item
                        name="mobile"
                        label="手机号码"
                        tooltip="方便系统联络该用户"
                        rules={[{ required: true, message: '请输入手机号码!' }]}
                    >
                        <Input prefix={<MobileOutlined />} placeholder="请输入手机号码" />
                    </Form.Item>

                    <Form.Item
                        name="password"
                        label="密码"
                        rules={[
                            { required: true, message: '请输入密码!' },
                            { max: 20, message: '长度不超过20个字符' }
                        ]}
                        hasFeedback
                    >
                        <Input.Password prefix={<LockOutlined />} placeholder="请输入密码" />
                    </Form.Item>

                    <Form.Item
                        name="confirm"
                        label="确认密码"
                        dependencies={['password']}
                        hasFeedback
                        rules={[
                            { required: true, message: '请确认密码!' },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('password') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('两次输入的密码不一致!'));
                                },
                            }),
                        ]}
                    >
                        <Input.Password prefix={<LockOutlined />} placeholder="请再次输入密码" />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="gender" label="性别">
                                <Select placeholder="请选择性别">
                                    <Option value="1">男</Option>
                                    <Option value="2">女</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="birthDate" label="出生日期">
                                <DatePicker style={{ width: '100%' }} placeholder="请选择出生日期" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="头像" tooltip="仅支持图片，不超过500KB，将被裁剪为200x200">
                                <ImgCrop rotationSlider>
                                    <Upload
                                        {...uploadProps('avatarUrl')}
                                        listType="picture-card"
                                        disabled={!username}
                                    >
                                        {avatarUrl ? <img src={avatarUrl} alt="avatar" style={{ width: '100%' }} /> : (
                                            <div>
                                                <UploadOutlined />
                                                <div style={{ marginTop: 8 }}>上传头像</div>
                                            </div>
                                        )}
                                    </Upload>
                                </ImgCrop>
                                {!username && <div style={{ color: '#999', fontSize: '12px' }}>请输入用户名后可用</div>}
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="微信二维码" tooltip="方便其他客户将来添加联络">
                                <ImgCrop rotationSlider aspect={1}>
                                    <Upload
                                        {...uploadProps('qrUrl', 'QrCode')}
                                        listType="picture-card"
                                        disabled={!username}
                                    >
                                        {qrUrl ? <img src={qrUrl} alt="qr" style={{ width: '100%' }} /> : (
                                            <div>
                                                <UploadOutlined />
                                                <div style={{ marginTop: 8 }}>上传二维码</div>
                                            </div>
                                        )}
                                    </Upload>
                                </ImgCrop>
                                {!username && <div style={{ color: '#999', fontSize: '12px' }}>请输入用户名后可用</div>}
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="verification"
                        label={`真人验证: ${mathChallenge.q}`}
                        rules={[{ required: true, message: '请输入计算结果!' }]}
                    >
                        <Input placeholder="请输入计算结果" />
                    </Form.Item>

                    <Form.Item>
                        <Button type="primary" htmlType="submit" loading={loading} block size="large">
                            注册
                        </Button>
                        <div style={{ marginTop: '10px', textAlign: 'center' }}>
                            已有账号？ <a onClick={() => navigate('/')}>去登录</a>
                        </div>
                    </Form.Item>
                </Form>
            </div>
        </div>
    );
};

export default RegisterPage;
